// src/components/profile/modals/ChangePasswordModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tabs, Tab, Spinner, InputGroup } from 'react-bootstrap';
import { changePassword } from '../../../services/profile.service';
import useModalHistory from '../../../hooks/useModalHistory';
import { AUTH_ENDPOINTS } from '../../../utils/constants';
import '../../../styles/pages/profile.css';

// Define Row and Col components at the top of the file before using them
const Row = ({ children, className }) => {
  return (
    <div className={`row ${className || ''}`}>
      {children}
    </div>
  );
};

const Col = ({ md, children, className }) => {
  return (
    <div className={`col-md-${md} ${className || ''}`}>
      {children}
    </div>
  );
};

const ChangePasswordModal = ({ show, onHide, token, showAlert }) => {
  // State for password method
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordOtp, setPasswordOtp] = useState('');
  const [showPasswordOtp, setShowPasswordOtp] = useState(false);

  // Password validation states
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: true,
    uppercase: true,
    lowercase: true,
    number: true,
    special: true,
    match: true
  });

  // State for recovery keys method
  const [keys, setKeys] = useState(Array(12).fill(''));
  const [newPasswordKeys, setNewPasswordKeys] = useState('');
  const [confirmPasswordKeys, setConfirmPasswordKeys] = useState('');
  const [keysOtp, setKeysOtp] = useState('');
  const [showKeysOtp, setShowKeysOtp] = useState(false);

  // Password validation states for keys tab
  const [passwordKeysRequirements, setPasswordKeysRequirements] = useState({
    length: true,
    uppercase: true,
    lowercase: true,
    number: true,
    special: true,
    match: true
  });

  // UI states
  const [activeTab, setActiveTab] = useState('password');
  const [loading, setLoading] = useState(false);

  // Password visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPasswordKeys, setShowNewPasswordKeys] = useState(false);
  const [showConfirmPasswordKeys, setShowConfirmPasswordKeys] = useState(false);

  // Fix tab content display issue with useEffect
  useEffect(() => {
    // Force tab content to display properly after render
    if (show) {
      const tabContent = document.querySelector('.password-modal-tabs .tab-content');
      if (tabContent) {
        const activePaneId = `${activeTab}-tab-pane`;
        const tabPanes = tabContent.querySelectorAll('.tab-pane');

        tabPanes.forEach(pane => {
          if (pane.id === activePaneId) {
            pane.classList.add('show', 'active');
            pane.style.display = 'block';
          } else {
            pane.classList.remove('show', 'active');
            pane.style.display = 'none';
          }
        });
      }
    }
  }, [show, activeTab]);

  // Reset validation when modal opens
  useEffect(() => {
    if (show) {
      // Reset password requirement states to default (all green)
      setPasswordRequirements({
        length: true,
        uppercase: true,
        lowercase: true,
        number: true,
        special: true
      });

      setPasswordKeysRequirements({
        length: true,
        uppercase: true,
        lowercase: true,
        number: true,
        special: true
      });
    }
  }, [show]);

  // Handle form reset when modal is closed
  const handleClose = () => {
    resetForm();
    onHide();
  };

  useModalHistory(show, onHide, 'change-password-modal');


  // Reset all form fields
  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordOtp('');
    setShowPasswordOtp(false);

    setKeys(Array(12).fill(''));
    setNewPasswordKeys('');
    setConfirmPasswordKeys('');

    setActiveTab('password');
    setLoading(false);

    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowNewPasswordKeys(false);
    setShowConfirmPasswordKeys(false);

    // Reset password requirement states to default (all green)
    setPasswordRequirements({
      length: true,
      uppercase: true,
      lowercase: true,
      number: true,
      special: true
    });

    setPasswordKeysRequirements({
      length: true,
      uppercase: true,
      lowercase: true,
      number: true,
      special: true
    });
  };

  // Handle password change using current password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!showPasswordOtp && !currentPassword) {
      showAlert('Please enter your current password', 'danger');
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlert('New password and confirm password do not match', 'danger');
      return;
    }

    if (!isValidPassword(newPassword)) {
      showAlert('Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character', 'danger');
      return;
    }

    setLoading(true);

    try {
      if (!showPasswordOtp) {
        // Initiate password change - request OTP
        const result = await changePassword(currentPassword, newPassword, null, token);

        if (result.success) {
          showAlert('OTP sent to your email', 'success');
          setShowPasswordOtp(true);
        } else {
          throw new Error(result.message || 'Failed to initiate password change');
        }
      } else {
        // Verify OTP and complete password change
        if (!passwordOtp) {
          showAlert('Please enter the OTP sent to your email', 'danger');
          setLoading(false);
          return;
        }

        const result = await changePassword(null, newPassword, passwordOtp, token);

        if (result.success) {
          showAlert('Password changed successfully', 'success');
          handleClose();
        } else {
          throw new Error(result.message || 'Failed to verify OTP');
        }
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showAlert(error.message || 'An error occurred while changing password', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Handle password change using recovery keys
  const handleKeysPasswordChange = async (e) => {
    e.preventDefault();

    // Basic validation
    // Check if all keys are filled
    if (keys.some(key => !key.trim())) {
      showAlert('Please enter all 12 recovery keys', 'danger');
      return;
    }

    if (newPasswordKeys !== confirmPasswordKeys) {
      showAlert('New password and confirm password do not match', 'danger');
      return;
    }

    if (!isValidPassword(newPasswordKeys)) {
      showAlert('Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character', 'danger');
      return;
    }

    setLoading(true);

    try {
      // Verify recovery keys and change password in one step
      const response = await fetch(AUTH_ENDPOINTS.VERIFY_MNEMONIC_CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: JSON.parse(sessionStorage.getItem('userDetails'))?.Email,
          mnemonicArray: keys,
          newPassword: newPasswordKeys
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showAlert('Password changed successfully', 'success');
        handleClose();
      } else {
        throw new Error(data.error || data.message || 'Failed to verify recovery keys');
      }
    } catch (error) {
      console.error('Error changing password with keys:', error);
      showAlert(error.message || 'An error occurred while changing password', 'danger');
    } finally {
      setLoading(false);
    }
  };


  // Handle key input changes
  const handleKeyChange = (index, value) => {
    const newKeys = [...keys];
    newKeys[index] = value;
    setKeys(newKeys);
  };

  // Helper to check if password meets requirements
  const isValidPassword = (password) => {
    const uppercasePattern = /[A-Z]/;
    const lowercasePattern = /[a-z]/;
    const numberPattern = /[0-9]/;
    const specialCharacterPattern = /[^A-Za-z0-9]/;
    const passwordLength = password.length >= 8;

    return passwordLength &&
      uppercasePattern.test(password) &&
      lowercasePattern.test(password) &&
      numberPattern.test(password) &&
      specialCharacterPattern.test(password);
  };

  // Validate password requirements
  const validatePasswordRequirements = (password, confirmValue, isKeysTab = false) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
      match: password === confirmValue && password !== ''
    };

    if (isKeysTab) {
      setPasswordKeysRequirements(requirements);
    } else {
      setPasswordRequirements(requirements);
    }

    return Object.values(requirements).every(req => req === true);
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    validatePasswordRequirements(value, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    validatePasswordRequirements(newPassword, value);
  };

  // Handle password keys input changes
  const handlePasswordKeysChange = (e) => {
    const value = e.target.value;
    setNewPasswordKeys(value);
    validatePasswordRequirements(value, confirmPasswordKeys, true);
  };

  const handleConfirmPasswordKeysChange = (e) => {
    const value = e.target.value;
    setConfirmPasswordKeys(value);
    validatePasswordRequirements(newPasswordKeys, value, true);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
    >
      <Modal.Header className="bg-dark text-white">
        <Modal.Title>Change Password</Modal.Title>
        <Button
          variant="link"
          className="close text-white"
          onClick={handleClose}
        >
          <span aria-hidden="true">&times;</span>
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          id="password-change-tabs"
          className="password-modal-tabs mb-3"
        >
          <Tab eventKey="password" title="Using Current Password">
            <Form onSubmit={handlePasswordSubmit} autoComplete="off">
              {!showPasswordOtp ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter your current password"
                        required
                      />
                      <InputGroup.Text
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className={`fas ${showCurrentPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter new password"
                        required
                      />
                      <InputGroup.Text
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm New Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        placeholder="Confirm new password"
                        required
                      />
                      <InputGroup.Text
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Enter OTP</Form.Label>
                  <Form.Control
                    type="text"
                    value={passwordOtp}
                    onChange={(e) => setPasswordOtp(e.target.value)}
                    placeholder="Enter the OTP sent to your email"
                    required
                  />
                  <Form.Text className="text-muted">
                    A one-time password (OTP) has been sent to your email. Please enter it here to complete the password change.
                  </Form.Text>
                </Form.Group>
              )}

              <div className="password-requirements mb-3">
                <h6>Password Requirements:</h6>
                <ul className="small password-requirements-list">
                  <li className={passwordRequirements.length ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordRequirements.length ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    At least 8 characters long
                  </li>
                  <li className={passwordRequirements.uppercase ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordRequirements.uppercase ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one uppercase letter (A-Z)
                  </li>
                  <li className={passwordRequirements.lowercase ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordRequirements.lowercase ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one lowercase letter (a-z)
                  </li>
                  <li className={passwordRequirements.number ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordRequirements.number ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one number (0-9)
                  </li>
                  <li className={passwordRequirements.special ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordRequirements.special ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one special character (!@#$%^&* etc.)
                  </li>
                  <li className={passwordRequirements.match ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordRequirements.match ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Passwords must match
                  </li>
                </ul>
              </div>

              <Button
                variant="success"
                type="submit"
                className="w-100"
                disabled={loading || !Object.values(passwordRequirements).every(req => req === true)}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="mr-2"
                    />
                    {showPasswordOtp ? 'Verifying...' : 'Processing...'}
                  </>
                ) : (
                  showPasswordOtp ? 'Verify OTP and Change Password' : 'Change Password'
                )}
              </Button>
            </Form>
          </Tab>

          <Tab eventKey="keys" title="Using Recovery Keys">
            <Form onSubmit={handleKeysPasswordChange} autoComplete="off">
              <p className="text-white mb-3">
                If you've forgotten your password, you can use your 12 recovery keys to set a new password.
              </p>

              <div className="recovery-keys mb-4 justify-content-center">
                <Row>
                  {/* Left Column - Keys 1-6 */}
                  <Col md={5}>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Form.Group className="mb-2" key={index}>
                        <Form.Control
                          type="text"
                          value={keys[index]}
                          onChange={(e) => handleKeyChange(index, e.target.value)}
                          placeholder={`Key ${index + 1}`}
                          className="key-input"
                          autoComplete="nope"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          required
                        />
                      </Form.Group>
                    ))}
                  </Col>

                  {/* Right Column - Keys 7-12 */}
                  <Col md={5}>
                    {Array.from({ length: 6 }).map((_, index) => {
                      const keyIndex = index + 6;
                      return (
                        <Form.Group className="mb-2" key={keyIndex}>
                          <Form.Control
                            type="text"
                            value={keys[keyIndex]}
                            onChange={(e) => handleKeyChange(keyIndex, e.target.value)}
                            placeholder={`Key ${keyIndex + 1}`}
                            className="key-input"
                            autoComplete="nope"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            required
                          />
                        </Form.Group>
                      );
                    })}
                  </Col>
                </Row>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showNewPasswordKeys ? "text" : "password"}
                    value={newPasswordKeys}
                    onChange={handlePasswordKeysChange}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    required
                  />
                  <InputGroup.Text
                    onClick={() => setShowNewPasswordKeys(!showNewPasswordKeys)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className={`fas ${showNewPasswordKeys ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showConfirmPasswordKeys ? "text" : "password"}
                    value={confirmPasswordKeys}
                    onChange={handleConfirmPasswordKeysChange}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    required
                  />
                  <InputGroup.Text
                    onClick={() => setShowConfirmPasswordKeys(!showConfirmPasswordKeys)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className={`fas ${showConfirmPasswordKeys ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <div className="password-requirements mb-3">
                <h6>Password Requirements:</h6>
                <ul className="small password-requirements-list">
                  <li className={passwordKeysRequirements.length ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordKeysRequirements.length ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    At least 8 characters long
                  </li>
                  <li className={passwordKeysRequirements.uppercase ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordKeysRequirements.uppercase ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one uppercase letter (A-Z)
                  </li>
                  <li className={passwordKeysRequirements.lowercase ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordKeysRequirements.lowercase ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one lowercase letter (a-z)
                  </li>
                  <li className={passwordKeysRequirements.number ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordKeysRequirements.number ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one number (0-9)
                  </li>
                  <li className={passwordKeysRequirements.special ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordKeysRequirements.special ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Must include at least one special character (!@#$%^&* etc.)
                  </li>
                  <li className={passwordKeysRequirements.match ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${passwordKeysRequirements.match ? 'fa-check' : 'fa-times'} mr-2`}></i>
                    Passwords must match
                  </li>
                </ul>
              </div>

              <Button
                variant="success"
                type="submit"
                className="w-100"
                disabled={loading || !Object.values(passwordKeysRequirements).every(req => req === true)}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="mr-2"
                    />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordModal;